import { HTMLAttributes, forwardRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'glass' | 'stat' | 'profile' | 'animated';
  hover?: boolean;
  glow?: boolean;
  centered?: boolean;
  animated?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', hover = false, glow = false, centered = false, animated = false, children, ...props }, ref) => {
    const variants = {
      default: 'card p-6',
      glass: 'glass-card p-6',
      stat: 'stat-card p-6',
      profile: 'card p-6 space-y-6',
      animated: 'animated-card',
    };

    const baseClasses = cn(
      variants[variant],
      hover && 'transition-all duration-300 ease-out hover:scale-[1.02] hover:border-primary-500/50',
      glow && 'hover:shadow-glow',
      centered && 'max-w-4xl mx-auto',
      className
    );

    if (animated) {
      return (
        <motion.div
          ref={ref as any}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
          className={baseClasses}
          {...(props as any)}
        >
          {children}
        </motion.div>
      );
    }

    return (
      <div
        ref={ref}
        className={baseClasses}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('card-header px-6 py-4 border-b border-surface-800', className)} {...props} />
  )
);

CardHeader.displayName = 'CardHeader';

const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('card-body p-6 space-y-4', className)} {...props} />
  )
);

CardBody.displayName = 'CardBody';

const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold text-surface-100 mb-2', className)}
      {...props}
    />
  )
);

CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-surface-400 mb-4', className)} {...props} />
  )
);

CardDescription.displayName = 'CardDescription';

// New: Card Footer component
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 py-4 border-t border-surface-800 bg-surface-900/50', className)} {...props} />
  )
);

CardFooter.displayName = 'CardFooter';

// Alias for CardBody for compatibility
const CardContent = CardBody;
CardContent.displayName = 'CardContent';

export { Card, CardHeader, CardBody, CardTitle, CardDescription, CardFooter, CardContent };
